import { createSelector } from 'reselect';
import startCase from 'lodash/startCase';
import uniq from 'lodash/uniq';
import zipObject from 'lodash/zipObject';
import defaults from 'lodash/defaults';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';

import { DBColumnDataType } from '@common/types/database';
import { paramSyntaxInCustomReport } from '../constants';
import { CustomReportTypes, CustomReportParamTypes, columnPivotOptions } from './constants';

import * as columnDefs from '../columnDefinitions'; // eslint-disable-line import/no-namespace
import { alignRightCellStyle, alignCenterCellStyle } from '@common/cellStyles';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import { CustomRenderer } from '../numeralRenderers';
import { formatPeriodCell, isPeriodFieldName } from '@common/utils/formatPeriodCell';


const filterReports = createSelector(
  customReports => customReports,
  customReports => customReports
    .filter(({ customReportType }) => customReportType === CustomReportTypes.REPORT)
);

export const getOptionsForReportSet = createSelector(
  filterReports,
  customReports => customReports.map(report => ({ label: report.reportSetName, value: report.reportSetId }))
);

export const getReportsIncludedInReportSet = createSelector(
  filterReports,
  (customReports, reportsId) => reportsId,
  (customReports, reportsId) => customReports.filter(customReport => reportsId.includes(customReport.reportSetId))
);

export const compareParams = (firstParam, secondParam) => {
  if (firstParam.type !== secondParam.type) {
    return false;
  }

  if (firstParam.type === CustomReportParamTypes.query && (
    firstParam.query.replace(/\s+/g, ' ').trim() !== secondParam.query.replace(/\s+/g, ' ').trim()
    || firstParam.label !== secondParam.label
    || firstParam.value !== secondParam.value
  )) {
    return false;
  }

  if (firstParam.name !== secondParam.name || firstParam.mappingName !== secondParam.mappingName) {
    return false;
  }

  return true;
};


export const getParamNamesForQuery = query => {
  const rawParams = query.match(paramSyntaxInCustomReport);

  if (!rawParams) {
    return [];
  }

  return rawParams && uniq(rawParams.map(param => param.slice(2, -1)));
};

export const getUniqueQueryParams = reports => {
  const initialResult = {
    uniqueParams: [],
    queryParams: {},
    paramToUniqueIdMapping: {},
  };

  const result = reports.reduce((result, report) => {
    Object.values(report.params)
      .forEach(param => {
        const existingParam = result.uniqueParams.find(uniqueParam => compareParams(uniqueParam, param));

        if (existingParam) {
          result.paramToUniqueIdMapping[report.reportSetId] = {
            ...result.paramToUniqueIdMapping[report.reportSetId],
            [param.name]: existingParam.id,
          };
        } else {
          const id = `${report.reportSetId}${param.type}_${param.name}`;
          if (param.type !== CustomReportParamTypes.validator) {
            result.uniqueParams.push({ ...param, id });
          }
          result.queryParams[id] = '';
          result.paramToUniqueIdMapping[report.reportSetId] = {
            ...result.paramToUniqueIdMapping[report.reportSetId],
            [param.name]: id,
          };
        }
      });

    return result;
  }, initialResult);

  return result;
};

export const cachedGetUniqueQueryParams = createSelector(
  reports => reports,
  getUniqueQueryParams
);

export const getColumnsHeaderName = columns => {
  if (!columns || !Array.isArray(columns)) {
    return [];
  }

  return columns.map(column => {
    const foundColumnDefinition = Object.values(columnDefs).find(
      colDef => colDef.field && colDef.field.toUpperCase() === column.toUpperCase()
    );
    return {
      field: column,
      headerName: foundColumnDefinition ? foundColumnDefinition.headerName : startCase(column),
    };
  });
};

export const getParamsForEachReport = ({
  reports,
  queryParams,
  mapping,
  generateFileName = () => '',
}) => reports.map(report => {
  const params = Object.values(report.params)
    .filter(param => param.type !== CustomReportParamTypes.validator)
    .reduce((result, param) => {
      const mappedParamId = mapping[report.reportSetId]?.[param.name];
      if (mappedParamId && Object.prototype.hasOwnProperty.call(queryParams, mappedParamId)) {
        result[param.name] = queryParams[mappedParamId];
      }
      return result;
    }, {});

  return {
    sql: report.sql,
    name: generateFileName(report.reportSetName, params),
    pillName: report.reportSetName,
    reportId: report.reportSetId,
    params,
    reportOptions: {
      columnDefinitions: getColumnsHeaderName(report.columns),
      filters: {},
    },
  };
});


export const getColumnDefs = ({ columns, types, filtersValues = {} }) =>
  columns.map(column => {
    const foundColumnDefinition = Object.values(columnDefs).find(
      colDef => colDef.field && colDef.field.toUpperCase() === column.toUpperCase()
    );

    const columnFiltersValues = filtersValues[column]
      ? {
        filterParams: {
          values: filtersValues[column],
        },
      }
      : {};

    if (types[column] === DBColumnDataType.NUMBER) {
      const cellRendererToAdd = {
        cellRenderer: CustomRenderer,
        exportFormat: CellExportFormats.NUMBER,
        ...alignRightCellStyle,
      };

      return foundColumnDefinition
        ? {
          ...columnPivotOptions,
          ...cellRendererToAdd,
          ...foundColumnDefinition,
          field: column,
          ...columnFiltersValues,
        }
        : {
          field: column,
          headerName: startCase(column),
          minWidth: 80,
          ...columnPivotOptions,
          ...cellRendererToAdd,
          ...columnFiltersValues,
        };
    }

    if (types[column] === DBColumnDataType.DATE && isPeriodFieldName(column)) {
      const pivotOptions = { enablePivot: true, enableRowGroup: true };

      return foundColumnDefinition
        ? {
          ...foundColumnDefinition,
          ...alignCenterCellStyle,
          customExport: { ...foundColumnDefinition.customExport, mapValue: formatPeriodCell },
          field: column,
          ...pivotOptions,
          ...columnFiltersValues,
        }
        : {
          field: column,
          headerName: startCase(column),
          minWidth: 100,
          ...alignCenterCellStyle,
          customExport: { mapValue: formatPeriodCell },
          ...pivotOptions,
          ...columnFiltersValues,
        };
    }

    const pivotOptions = { enablePivot: true, enableRowGroup: true };

    return foundColumnDefinition
      ? {
        ...foundColumnDefinition,
        field: column,
        ...pivotOptions,
        ...columnFiltersValues,
      }
      : {
        field: column,
        headerName: startCase(column),
        minWidth: 80,
        ...pivotOptions,
        ...columnFiltersValues,
      };
  });

export const getFiltersValues = reports => {
  const filterValues = {};
  reports.forEach(({ rows, columns }) => {
    columns.forEach(column => {
      if (!filterValues[column]) {
        filterValues[column] = new Set();
      }
    });
    rows.forEach(reportRow => {
      columns.forEach(column => {
        filterValues[column].add(reportRow[column]);
      });
    });
  });
  return Object.entries(filterValues).reduce((result, [key, values]) => ({
    ...result,
    [key]: Array.from(values),
  }), {});
};

const createControlsFor = params => zipObject(params, params.map(name => ({ name, mappingName: name, type: 'input' })));

export const updateParamsFormData = (formData, query, newParam = null) => {
  const updatedFormData = {
    ...formData,
    ...newParam ? { [newParam.name]: newParam } : {},
  };
  const baseQueryParams = getParamNamesForQuery(query);
  const baseQueryParamsFormData = pick(updatedFormData, baseQueryParams);
  const subQueriesParams = [...Object.values(baseQueryParamsFormData)].flatMap(
    ({ query }) => query ? getParamNamesForQuery(query) : []
  );
  const uniqueParams = uniq([...baseQueryParams, ...subQueriesParams]);

  const validatorParams = Object.keys(updatedFormData).filter(paramName =>
    updatedFormData[paramName] && updatedFormData[paramName].type === CustomReportParamTypes.validator
  );
  const allParamsToKeep = uniq([...uniqueParams, ...validatorParams]);
  const existingControls = pick(updatedFormData, allParamsToKeep);
  return defaults({ ...existingControls }, createControlsFor(uniqueParams));
};

export const updateQueryParamValues = (queryParams, newParamsFormData) => mapValues(
  newParamsFormData,
  ({ name }) => queryParams[name] ? queryParams[name] : ''
);
